import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Facebook, Menu, X, Info, MessageCircle } from "lucide-react";
import { BIZ, SERVICES, PRODUCTS, STEPS_PAWN, STEPS_JEWELRY } from "./agenciabelenData";
import ConceptCTAPill from "@/components/ConceptCTAPill";

const GREEN = "#1E4D3A";
const GOLD = "#C79A3E";
const CREAM = "#FBF6EA";

const fadeUp = { initial: { opacity: 0, y: 22 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.55, ease: "easeOut" } };

export default function AgenciabelenSite() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("jewelry");
  const links = [["About", "#about"], ["Jewelry", "#jewelry"], ["Pawnshop", "#pawnshop"], ["Visit", "#visit"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: "#1C2A22", fontFamily: "'Mulish', sans-serif" }} data-testid="agenciabelen-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Mulish:wght@400;500;600;700&display=swap'); .ab-serif{font-family:'Playfair Display',serif;}`}</style>

      <div data-testid="jewelry-disclaimer" className="bg-black text-white text-center text-xs py-2.5">
        <span className="inline-flex items-center gap-2"><Info size={13} style={{ color: GOLD }} />Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {BIZ.name}.</span>
      </div>

      <header className="sticky top-0 z-40 bg-[#FBF6EA]/95 backdrop-blur shadow-sm">
        <nav className="max-w-6xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="agenciabelen-logo" className="ab-serif text-lg font-bold" style={{ color: GREEN }}>Agencia Belen</a>
          <div className="hidden md:flex items-center gap-8">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-medium text-[#3A4A40] hover:text-[#1E4D3A]">{l}</a>)}
          </div>
          <a href="#visit" data-testid="agenciabelen-header-cta" className="hidden sm:inline-flex px-6 py-2.5 text-sm font-semibold text-white" style={{ background: GREEN }}>Visit Us</a>
          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="agenciabelen-menu-toggle" aria-label="Menu">{open ? <X size={24} /> : <Menu size={24} />}</button>
        </nav>
        {open && <div className="md:hidden px-5 pb-4 space-y-1 bg-[#FBF6EA]">{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium">{l}</a>)}</div>}
      </header>

      {/* HERO — heritage trust, Cebuano motto front and center */}
      <section className="py-24 sm:py-28 text-center" style={{ background: GREEN }} data-testid="agenciabelen-hero">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp}>
            <span className="text-xs uppercase tracking-[0.25em]" style={{ color: GOLD }}>Perdices St. · Dumaguete City</span>
            <h1 className="mt-6 ab-serif text-3xl sm:text-4xl italic text-white leading-relaxed">"{BIZ.tagline}"</h1>
            <p className="mt-6 text-white/70 max-w-xl mx-auto">Jewelry sales, appraisals and pawnshop services, trusted in Dumaguete for years.</p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <a href="#jewelry" data-testid="agenciabelen-hero-cta" className="px-8 py-3.5 text-sm font-semibold" style={{ background: GOLD, color: GREEN }}>Explore Our Jewelry</a>
              <a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="agenciabelen-hero-message" className="px-8 py-3.5 text-sm font-semibold text-white border border-white/30">Message Us</a>
            </div>
            <p className="mt-8 text-sm text-white/50">{BIZ.rating}★ Rating · {BIZ.reviewCount} Reviews</p>
          </motion.div>
        </div>
      </section>

      <section id="about" className="max-w-4xl mx-auto px-5 sm:px-8 py-16 text-center">
        <motion.p {...fadeUp} className="text-xl leading-relaxed" style={{ color: GREEN }}>{BIZ.intro}</motion.p>
      </section>

      {/* WEDDING RINGS — the clinic's own real promo asset */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-16">
        <motion.img {...fadeUp} src="/agenciabelen/promo-wedding.jpg" alt="Wedding rings available at Agencia Belen Pawnshop & Jewelry" className="w-full shadow-md" />
      </section>

      {/* SERVICES TAB */}
      <section id="jewelry" className="py-16" style={{ background: "#EFE7D2" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="flex justify-center gap-2 mb-8">
            <button data-testid="agenciabelen-tab-jewelry" onClick={() => setTab("jewelry")} className="px-5 py-2.5 text-sm font-semibold rounded-full" style={tab === "jewelry" ? { background: GREEN, color: "white" } : { background: "white", color: "#3A4A40" }}>Jewelry</button>
            <button data-testid="agenciabelen-tab-pawnshop" onClick={() => setTab("pawnshop")} className="px-5 py-2.5 text-sm font-semibold rounded-full" style={tab === "pawnshop" ? { background: GREEN, color: "white" } : { background: "white", color: "#3A4A40" }}>Pawnshop</button>
          </div>
          <div className="grid sm:grid-cols-2 gap-6 min-h-[130px]" data-testid="agenciabelen-services-grid">
            {SERVICES.filter((s) => s.cat === tab).map((s) => (
              <motion.div key={s.title} {...fadeUp} className="p-6 bg-white shadow-sm">
                <h3 className="ab-serif font-semibold" style={{ color: GREEN }}>{s.title}</h3>
                <p className="mt-1.5 text-sm text-[#3A4A40]/80">{s.text}</p>
              </motion.div>
            ))}
          </div>
          <div id="pawnshop" className="mt-14">
            <motion.h3 {...fadeUp} className="ab-serif text-2xl text-center mb-8" style={{ color: GREEN }}>{tab === "jewelry" ? "How to Buy Jewelry" : "How the Pawnshop Works"}</motion.h3>
            <div className="grid sm:grid-cols-4 gap-6">
              {(tab === "jewelry" ? STEPS_JEWELRY : STEPS_PAWN).map((s) => (
                <motion.div key={s.n} {...fadeUp} className="text-center">
                  <div className="ab-serif text-2xl font-bold" style={{ color: GOLD }}>{s.n}</div>
                  <p className="mt-2 font-semibold text-sm">{s.title}</p>
                  <p className="mt-1 text-xs text-[#3A4A40]/70">{s.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <motion.h2 {...fadeUp} className="ab-serif text-3xl font-bold text-center mb-12" style={{ color: GREEN }}>Jewelry &amp; Branded Watches</motion.h2>
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
          {PRODUCTS.map((p) => (
            <motion.a key={p.name} {...fadeUp} href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="agenciabelen-product-card" className="bg-white shadow-sm group">
              <div className="overflow-hidden"><img src={p.img} alt={p.name} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" /></div>
              <div className="p-4">
                <h3 className="text-sm font-semibold">{p.name}</h3>
                <p className="mt-3 text-xs font-semibold" style={{ color: "#8A6B0A" }}>Ask About This Piece &rarr;</p>
              </div>
            </motion.a>
          ))}
        </div>
      </section>

      {/* VISIT */}
      <section id="visit" className="py-16" style={{ background: GREEN }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-12">
          <motion.div {...fadeUp}>
            <h2 className="ab-serif text-3xl font-bold text-white">Visit Us</h2>
            <ul className="mt-8 space-y-5 text-white/80">
              <li className="flex items-start gap-4"><MapPin size={20} style={{ color: GOLD }} className="mt-0.5" />{BIZ.address}</li>
              <li className="flex items-start gap-4"><Phone size={20} style={{ color: GOLD }} className="mt-0.5" /><a href={`tel:${BIZ.phone.replace(/\s/g, "")}`} className="hover:underline">{BIZ.phone}</a></li>
              <li className="flex items-start gap-4"><Facebook size={20} style={{ color: GOLD }} className="mt-0.5" /><a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="agenciabelen-facebook-link" className="hover:underline">PawnHere6200</a></li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp} className="min-h-[300px] rounded-xl overflow-hidden shadow-md"><iframe title="Agencia Belen map" src={BIZ.mapEmbed} className="w-full h-full min-h-[300px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" /></motion.div>
        </div>
      </section>

      <footer className="py-8 bg-[#FBF6EA]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs text-[#5A6A5E]">
          <p className="ab-serif text-base font-bold" style={{ color: GREEN }}>Agencia Belen Pawnshop &amp; Jewelry</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {BIZ.name}. Confirm pricing and terms in person.</p>
        </div>
      </footer>

      <a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="agenciabelen-sticky-contact" className="md:hidden fixed bottom-4 inset-x-4 z-50 px-6 py-3.5 text-center text-sm font-semibold text-white shadow-xl flex items-center justify-center gap-2" style={{ background: GREEN }}><MessageCircle size={17} /> Message Agencia Belen</a>
      <ConceptCTAPill slug="agencia-belen-pawnshop-concept" />
    </div>
  );
}
